import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Combobox, TextInput, useLocale } from '@ministry/ui/internal';

import { listOrgUnits, type OrgUnit } from '../../lib/admin-org.js';
import { ApiError } from '../../lib/api-client.js';
import { useT } from '../../i18n/strings.js';

/**
 * A searchable org-unit picker backed by `GET /org-units` (`org.admin`), shared by the
 * user profile form (`primaryOrgUnitId`) and the role-grant form (`scopeOrgUnitId`,
 * R-044's anchor). Both `admin_hr` and `super_admin` hold `org.admin` at the same scope
 * `user.admin` is held at (`authorization-matrix.ts`), so this succeeds for every caller
 * who can reach either form in practice — but a caller who genuinely lacks `org.admin`
 * gets a plain UUID field instead of a silently broken combobox, never a crash.
 */
export interface OrgUnitPickerProps {
  id: string;
  label: string;
  value: string | undefined;
  onValueChange: (value: string) => void;
  required?: boolean;
  errorText?: string;
  helperText?: string;
  /** Only offer live (non-archived) units — the sensible default everywhere this is used. */
  includeArchived?: boolean;
}

export function OrgUnitPicker({
  id,
  label,
  value,
  onValueChange,
  required,
  errorText,
  helperText,
  includeArchived = false,
}: OrgUnitPickerProps): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const query = useQuery({
    queryKey: ['admin-org-units-picker', includeArchived],
    queryFn: ({ signal }) => listOrgUnits(includeArchived, signal),
    retry: false,
  });

  const forbidden = query.isError && query.error instanceof ApiError && query.error.code.startsWith('FORBIDDEN');

  if (forbidden) {
    return (
      <TextInput
        id={id}
        label={label}
        required={required}
        value={value ?? ''}
        onChange={(event) => onValueChange(event.target.value)}
        errorText={errorText}
        helperText={helperText ?? t('admin.orgUnitPicker.manualEntryHelp')}
      />
    );
  }

  const options = (query.data ?? []).map((unit: OrgUnit) => ({
    value: unit.id,
    label: `${locale === 'ne' ? unit.nameNe : unit.nameEn} (${unit.code})`,
  }));

  return (
    <Combobox
      id={id}
      label={label}
      required={required}
      options={options}
      value={value}
      onValueChange={onValueChange}
      loading={query.isLoading}
      errorText={errorText ?? (query.isError ? t('admin.orgUnitPicker.loadError') : undefined)}
      helperText={helperText}
    />
  );
}
